import { Exclude } from 'class-transformer'

import { logger } from '../util'
import { Message } from './Message'

export class Timing {
  /**
   *
   * @returns UTC Now in Milliseconds
   */
  static getUtcMsNow() {
    const checktime = new Date()
    // return checktime.getTime() + checktime.getTimezoneOffset() * 60 * 1000
    return checktime.getTime()
  }

  static over10SecondsAgo(utcms: number) {
    return Timing.overXSecondsAgo(utcms, 10)
  }

  static over20SecondsAgo(utcms: number) {
    return Timing.overXSecondsAgo(utcms, 20)
  }

  static over30SecondsAgo(utcms: number) {
    return Timing.overXSecondsAgo(utcms, 30)
  }

  static overAMinuteAgo(utcms: number) {
    return Timing.overXSecondsAgo(utcms, 60)
  }

  static overXSecondsAgo(utcms: number, secs: number) {
    return Timing.getUtcMsNow() - secs * 1000 > utcms
  }
}

export interface Event {
  sourceId: string
  sourceName: string
  name: string
}

export interface VersionedEvent extends Event {
  version: number
}

export interface EventSourced {
  getId(): string
  getVersion(): number
  getEvents(): VersionedEvent[]
}

export class Evented implements EventSourced {
  private version: number
  public id: string
  public checkpoint: number

  // @TODO: Not sure we need this....
  // https://github.com/Poker-Powher/poker-powher/commit/83c83a836ce832a190971d7ffd482c3066fa3385
  @Exclude({ toPlainOnly: true })
  private events: VersionedEvent[]

  constructor(id: string) {
    this.checkpoint = 0
    this.id = id
    this.version = 0
    this.events = []
    this.doCheckpoint()
  }

  setId(id: string) {
    this.id = id
  }

  getId(): string {
    return this.id
  }

  getVersion(): number {
    return this.version
  }

  getEvents(): VersionedEvent[] {
    this.events.sort((a, b) => {
      if (a.version > b.version) {
        return 1
      }
      if (a.version < b.version) {
        return -1
      }
      return 0
    })
    return this.events
  }

  /**
   * Load events for an `Evented` class.
   * Resets events and version
   * @param events Events to reload into state
   * @param reload Whether to clear events before loading events
   */
  loadState(events: VersionedEvent[], reload = true) {
    if (reload) {
      this.events = []
      this.version = 0
    }

    for (const e of events) {
      this.getHandler(e.name)(e)
      this.version = e.version
      this.events.push(e)
    }
  }

  updateState(event: VersionedEvent, shouldUpdateCheckpoint = true) {
    event.sourceId = this.id
    event.version = this.version + 1
    if (shouldUpdateCheckpoint) {
      this.doCheckpoint()
    }
    this.getHandler(event.name)(event)
    this.version = event.version
    this.events.push(event)

    return {
      sourceId: event.sourceId,
      version: event.version
    }
  }

  lastCheckpoint() {
    return this.checkpoint
  }

  setCheckpointManually(utcms: number) {
    this.checkpoint = utcms
  }

  currentCheckpoint() {
    return Timing.getUtcMsNow()
  }

  /**
   * Returns the event handler name, if the event name
   * contains colons, it massages it to a method name
   * (e.g. poker:game:created -> onPokerGameCreated)
   * @param name Event name
   * @returns Handler name to call
   */
  private getHandler(name: string) {
    // logger.trace(`[EventSourced.getHandler] handler name: ${name}`)
    let handlerName = name
    if (name.indexOf(':') > 0) {
      handlerName = name
        .split(':')
        .map((part) => {
          return part.charAt(0).toUpperCase() + part.slice(1)
        })
        .join('')
    }

    logger.trace(`[EventSourced.getHandler] handler name: ${name} >> ${handlerName}`)
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    return this[`on${handlerName}`].bind(this)
  }

  /**
   * Update the checkpoint for this evented
   */
  private doCheckpoint() {
    this.checkpoint = Timing.getUtcMsNow()
  }
}

export interface EventHandler {
  handleEvent(event: Message<Event>, callback: (error: Error) => void): void
}
